import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { AppButton } from '../button/app-button';
import { ModalPortal } from '../modal/modal-portal';
import { useDialogFocus } from '../hooks/use-dialog-focus';
import { Presence, scrimMotion, useOverlayMotion } from '../../motion';
import {
  Card,
  Controls,
  ControlLabel,
  ControlRow,
  Counter,
  CropStage,
  ErrorText,
  Footer,
  Header,
  Hint,
  IconButton,
  Scrim,
  Slider,
  Title,
} from './image-crop-modal.styled';

/**
 * The crop step. One photo at a time, square, then a WebP blob.
 *
 * WHY THE CROP HAPPENS HERE AND NOT ON THE SERVER
 *
 * The server stores exactly the bytes it is given and derives nothing. That is
 * a deliberate trade with three payoffs and one cost, and the cost is the one
 * to know about:
 *
 *   + The upload is small. A 12 MP phone photo is ~4 MB; this sends ~150 KB,
 *     which matters on a phone at the front desk of a building with bad signal.
 *   + No server-side image library. `sharp` is a native dependency that has to
 *     compile on the deploy box, and the 1 GiB instance decoding a 12 MP JPEG
 *     is a memory spike per concurrent upload.
 *   + EXIF is dropped for free. A canvas re-encode carries no metadata, so the
 *     GPS coordinates a phone stamps into a photo of a building never leave the
 *     browser. That is a privacy win nobody has to remember to implement.
 *   - The original is gone. A re-crop later means re-uploading the source photo.
 *     Accepted: this is a gallery, not a photo editor.
 *
 * WHY 1:1
 *
 * Every tile in the grid, every cover thumbnail and every avatar-sized preview
 * is square. A gallery of mixed aspect ratios needs either letterboxing (grey
 * bars, cheap) or object-fit cropping at display time (the subject drifts out
 * of frame, and nobody can fix it). Cropping once, at upload, with the user
 * choosing the framing, is the only version where what they see is what gets
 * stored.
 */

// 1200 is the largest a square tile is ever rendered at (a full-bleed lightbox
// on a 2x display), so anything above it is bytes nobody sees.
const OUTPUT_SIZE = 1200;
const OUTPUT_TYPE = 'image/webp';
const OUTPUT_QUALITY = 0.85;

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;

export interface CropModalProps {
  // The queue. The modal walks it one file at a time and calls `onComplete`
  // once, with every cropped blob, so the caller does a single batched upload.
  files: File[];
  open: boolean;
  onCancel: () => void;
  onComplete: (blobs: Blob[]) => void;
}

// Turn the crop rectangle react-easy-crop reports (in SOURCE pixel space) into
// a square WebP blob at OUTPUT_SIZE.
//
// The rotation handling is the fiddly part: the canvas is first sized to the
// rotated bounding box and the image drawn about its centre, and only then is
// the crop rectangle read back out. Cropping first and rotating after produces
// clipped corners on any angle that is not a multiple of 180.
const renderCrop = async (
  imageSrc: string,
  crop: Area,
  rotation: number
): Promise<Blob> => {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () =>
      reject(new Error('That file could not be read as an image'))
    );
    img.src = imageSrc;
  });

  const radians = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  const boxWidth = image.width * cos + image.height * sin;
  const boxHeight = image.width * sin + image.height * cos;

  const stage = document.createElement('canvas');
  stage.width = boxWidth;
  stage.height = boxHeight;
  const stageCtx = stage.getContext('2d');
  if (!stageCtx) throw new Error('Your browser could not process this image');

  stageCtx.translate(boxWidth / 2, boxHeight / 2);
  stageCtx.rotate(radians);
  stageCtx.drawImage(image, -image.width / 2, -image.height / 2);

  const out = document.createElement('canvas');
  out.width = OUTPUT_SIZE;
  out.height = OUTPUT_SIZE;
  const outCtx = out.getContext('2d');
  if (!outCtx) throw new Error('Your browser could not process this image');

  // Smoothing matters here: the source crop is usually LARGER than 1200px, and
  // an unsmoothed downscale of a photo aliases badly on brickwork and railings
  // — exactly what building photos are full of.
  outCtx.imageSmoothingEnabled = true;
  outCtx.imageSmoothingQuality = 'high';
  outCtx.drawImage(
    stage,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE
  );

  // toBlob reports EVERY failure the same way: it hands back null. That covers
  // an out-of-memory canvas, a tainted canvas, and a browser that cannot encode
  // the requested type — so the fallback below is not defensive padding, it is
  // the only way to tell the last case apart from the others.
  const encode = (type: string) =>
    new Promise<Blob | null>((resolve) => {
      out.toBlob((blob) => resolve(blob), type, OUTPUT_QUALITY);
    });

  const webp = await encode(OUTPUT_TYPE);
  if (webp && webp.size > 0) return webp;

  // WebP is ~30% smaller and supported everywhere current, but an older
  // in-app browser (some Android WebViews, older Safari) silently returns null
  // for it. Falling back to JPEG keeps those users able to add a photo at all —
  // the server sniffs the real bytes and accepts either.
  const jpeg = await encode('image/jpeg');
  if (jpeg && jpeg.size > 0) return jpeg;

  throw new Error(
    'Your browser could not process that image. Try a different photo, or a different browser.'
  );
};

export const ImageCropModal: React.FC<CropModalProps> = ({
  files,
  open,
  onCancel,
  onComplete,
}) => {
  const [index, setIndex] = useState(0);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(ZOOM_MIN);
  const [rotation, setRotation] = useState(0);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Blobs already cropped in this run. Held in a ref, not state: `handleNext`
  // reads it immediately after appending, and a state update would still be
  // showing the previous array at that point.
  const doneRef = useRef<Blob[]>([]);

  const dialogRef = useDialogFocus<HTMLDivElement>(open, onCancel);
  const cardMotion = useOverlayMotion('modal');

  const current = files[index];

  // Reset per file, and revoke the previous object URL. Without the revoke, a
  // session of cropping twenty photos keeps twenty decoded images alive for as
  // long as the tab is open.
  useEffect(() => {
    if (!open || !current) return undefined;

    const url = URL.createObjectURL(current);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(ZOOM_MIN);
    setRotation(0);
    setAreaPixels(null);
    setError('');

    return () => URL.revokeObjectURL(url);
  }, [open, current]);

  // A fresh run every time the modal opens, so a cancelled batch does not leak
  // its blobs into the next one.
  useEffect(() => {
    if (open) {
      setIndex(0);
      doneRef.current = [];
    }
  }, [open]);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setAreaPixels(pixels);
  }, []);

  const handleNext = async () => {
    if (!areaPixels || busy) return;

    setBusy(true);
    setError('');
    try {
      const blob = await renderCrop(imageSrc, areaPixels, rotation);
      doneRef.current = [...doneRef.current, blob];

      if (index + 1 < files.length) {
        setIndex(index + 1);
      } else {
        onComplete(doneRef.current);
      }
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Could not prepare that image'
      );
    } finally {
      setBusy(false);
    }
  };

  // Skipping is not the same as cancelling. One unreadable file in a batch of
  // twelve must not throw away the eleven already cropped.
  const handleSkip = () => {
    if (index + 1 < files.length) {
      setIndex(index + 1);
    } else if (doneRef.current.length > 0) {
      onComplete(doneRef.current);
    } else {
      onCancel();
    }
  };

  const isLast = index + 1 === files.length;

  // The conditional is inside the Presence: cancelling or finishing plays the
  // exit before the portal unmounts.
  return (
    <Presence>
      {open && current ? (
    <ModalPortal key="image-crop-modal">
      <Scrim
        {...scrimMotion}
        onMouseDown={(event) => event.target === event.currentTarget && onCancel()}
      >
        <Card
          {...cardMotion}
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Position and crop the photo"
        >
          <Header>
            <Title>Position the photo</Title>
            {files.length > 1 ? (
              <Counter>
                {index + 1} of {files.length}
              </Counter>
            ) : null}
          </Header>

          <CropStage>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              minZoom={ZOOM_MIN}
              maxZoom={ZOOM_MAX}
              restrictPosition
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
            />
          </CropStage>

          <Controls>
            <ControlRow>
              <ControlLabel htmlFor="crop-zoom">Zoom</ControlLabel>
              <Slider
                id="crop-zoom"
                type="range"
                min={ZOOM_MIN}
                max={ZOOM_MAX}
                step={0.01}
                value={zoom}
                onChange={(event) => setZoom(Number(event.target.value))}
              />
            </ControlRow>

            <ControlRow>
              <ControlLabel htmlFor="crop-rotate">Rotate</ControlLabel>
              <Slider
                id="crop-rotate"
                type="range"
                min={0}
                max={360}
                step={1}
                value={rotation}
                onChange={(event) => setRotation(Number(event.target.value))}
              />
              <IconButton
                type="button"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                aria-label="Rotate 90 degrees"
              >
                90°
              </IconButton>
              <IconButton
                type="button"
                onClick={() => {
                  setRotation(0);
                  setZoom(ZOOM_MIN);
                  setCrop({ x: 0, y: 0 });
                }}
              >
                Reset
              </IconButton>
            </ControlRow>

            <Hint>
              Drag to reposition, pinch or scroll to zoom. The square you see is
              exactly what gets saved.
            </Hint>

            {error ? <ErrorText role="alert">{error}</ErrorText> : null}
          </Controls>

          <Footer>
            <AppButton variant="ghost" onClick={onCancel} disabled={busy}>
              Cancel
            </AppButton>
            {files.length > 1 ? (
              <AppButton variant="secondary" onClick={handleSkip} disabled={busy}>
                Skip this one
              </AppButton>
            ) : null}
            <AppButton
              variant="primary"
              onClick={handleNext}
              loading={busy}
              loadingLabel="Preparing"
              disabled={!areaPixels}
            >
              {isLast ? 'Save photo' : 'Next photo'}
            </AppButton>
          </Footer>
        </Card>
      </Scrim>
    </ModalPortal>
      ) : null}
    </Presence>
  );
};
